/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {LocalCreativePreview} from '../../../components/session/local-creative-preview-data';
import {
  hasSpecificPreviewLanguageSelection,
  normalizePreviewLanguageSelection,
  PREVIEW_LANGUAGE_OPTIONS,
  resolvePreviewLanguageCode
} from '../../creative/ad/preview-language';
import {doPreview} from './creative-preview-runner';
import template from './nude-preview-page.component.html';
import {BrowserLocationService, WINDOW} from '../../angular-platform-providers';
import {CreativePreviewApiService} from '../../creative/creative-preview-api.service';
import {ApiConfig} from '../../config/api-config';

export function shouldDisplayLanguageSelector(layoutType: string, previewData: any): boolean {
  if (layoutType === 'VAST') {
    return false;
  }
  return !!previewData.showLanguageSelector
    || hasSpecificPreviewLanguageSelection(previewData.previewLanguage)
    || !!previewData.dataLang;
}

@Component({
  standalone: false,
  selector: 'adn-nude-preview-page',
  template
})
export class NudePreviewPageComponent implements OnInit {
  layoutType: string;
  previewLanguageOptions = PREVIEW_LANGUAGE_OPTIONS;
  showLanguageSelector = false;
  previewLanguage: string;
  hasError: string;
  private readonly publicErrorState = signal<string | null>(null);
  private readonly errorMessageState = signal<any>(null);

  get publicError(): string | null {
    return this.publicErrorState();
  }

  set publicError(value: string | null) {
    this.publicErrorState.set(value);
  }

  get errorMessage(): any {
    return this.errorMessageState();
  }

  set errorMessage(value: any) {
    this.errorMessageState.set(value);
  }
  checkingTheClick: string;
  redirectUrl: string;
  checkUrl = false;

  private previewData: any = {};
  constructor(
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly creativePreviewApi: CreativePreviewApiService,
    private readonly uiRouter: UIRouter,
    private readonly apiConfig: ApiConfig
  ) {
  }

  ngOnInit(): void {
    const routeParams = this.uiRouter.globals.params;
    const uuid = routeParams.uuid;
    const spCreativeId = routeParams.creativeId;
    const spNetworkId = routeParams.networkId;
    const hasDirectInfo = spCreativeId && spNetworkId;

    this.layoutType = routeParams.layoutType;

    let previewData: any = {};
    const localCreativePreview = uuid ? new LocalCreativePreview().get(uuid) : null;
    previewData.networkId = _.get(localCreativePreview, ['networkId']) || spNetworkId;
    previewData.creativeWidth = routeParams.creativeWidth;
    previewData.creativeHeight = routeParams.creativeHeight;
    previewData.creativeId = spCreativeId;
    previewData.lineItemId = routeParams.lineItemId;
    previewData.lineItemCode = routeParams.lineItemCode;
    if (localCreativePreview) {
      previewData = _.assign(previewData, _.get(localCreativePreview, ['previewData']) || {}, {networkId: previewData.networkId});
    }
    if (!_.isUndefined(routeParams.dataLang) && routeParams.dataLang !== null) {
      previewData.dataLang = routeParams.dataLang || null;
    }
    if (!_.isUndefined(routeParams.previewLanguage) && routeParams.previewLanguage !== null) {
      previewData.previewLanguage = routeParams.previewLanguage || null;
    }

    this.showLanguageSelector = shouldDisplayLanguageSelector(this.layoutType, previewData);
    const previewLanguageValues = _.map(PREVIEW_LANGUAGE_OPTIONS, 'value');
    const previewLanguageFallback = previewLanguageValues.indexOf(previewData.dataLang) > -1 ? previewData.dataLang : null;
    this.previewLanguage = normalizePreviewLanguageSelection(previewData.previewLanguage || previewLanguageFallback);

    const absUrlSplit = this.location.href.split('&');
    this.checkingTheClick = this.findQueryParamValueManually(absUrlSplit, 'checkingTheClick');
    this.redirectUrl = decodeURIComponent(this.findQueryParamValueManually(absUrlSplit, 'redirectUrl'));
    this.location.setQueryParam('checkingTheClick', null);
    this.location.setQueryParam('redirectUrl', null);

    if (!uuid && !hasDirectInfo) {
      this.hasError = 'MISSING_UUID';
      return;
    }

    this.previewData = previewData;
    if (!previewData.networkId || !previewData.creativeId) {
      this.hasError = 'MISSING_INFO';
      return;
    }
    this.renderPreview();
  }

  updateUrl(): void {
    const specialQp = 'preview-redirect-to-test-click-handling';
    if (this.checkUrl) {
      this.location.setQueryParam(specialQp, this.location.href + '&checkingTheClick=true');
    } else {
      this.location.setQueryParam(specialQp, null);
    }
    this.renderPreview();
  }

  onPreviewLanguageChange(): void {
    this.previewLanguage = normalizePreviewLanguageSelection(this.previewLanguage);
    this.location.setQueryParam('previewLanguage', this.previewLanguage);
    this.location.setQueryParam('dataLang', this.previewLanguage === 'browser' ? null : this.previewLanguage);
    this.renderPreview();
  }

  private findQueryParamValueManually(split: string[], name: string): string {
    const values = _.map(_.filter(split, (s) => {
      const subSplit = s.split('=');
      return subSplit[0] === name && subSplit.length === 2;
    }), (entry) => entry.split('=')[1]);
    return values && values.length === 1 ? values[0] : '';
  }

  private getSelectedLanguageCode(): string | null {
    return this.showLanguageSelector
      ? resolvePreviewLanguageCode(this.previewLanguage, this.window.navigator)
      : null;
  }

  private renderPreview(): void {
    this.publicError = null;
    this.errorMessage = null;
    this.previewLanguage = normalizePreviewLanguageSelection(this.previewLanguage);
    this.previewData.previewLanguage = this.previewLanguage;
    this.previewData.dataLang = this.getSelectedLanguageCode();
    this.previewData.dataLangAttribute = hasSpecificPreviewLanguageSelection(this.previewLanguage)
      ? this.previewData.dataLang
      : null;
    // Public /preview-creative always allows scripts (isolateFrame/blockScript off).
    this.previewData.allowScript = true;
    doPreview(
      this.window,
      null,
      this.location,
      null,
      null,
      this.previewData,
      true,
      this.creativePreviewApi,
      this.apiConfig,
      this
    );
  }
}
